import { useState } from 'react';
import { Check } from 'lucide-react';
import { useToast } from '@/components/Toast';
import { MultiClientPicker, type SelectedClient } from '@/components/MultiClientPicker';
import { useClientEngagements, useSubmitDiscontinuation, type EngagementRow } from '@/api/hooks';
import { ApiError } from '@/api/client';

const todayISO = () => new Date().toISOString().slice(0, 10);

export function DiscontinuationRequest() {
  const notify = useToast();
  const submit = useSubmitDiscontinuation();
  const [clientSel, setClientSel] = useState<SelectedClient[]>([]);
  const client = clientSel[0] ?? null;
  const engagements = useClientEngagements(client?.clientId);
  const [selected, setSelected] = useState<Record<string, EngagementRow>>({});
  const [effectiveDate, setEffectiveDate] = useState(todayISO());
  const [notes, setNotes] = useState('');

  const engList = engagements.data ?? [];
  const invalid = !client || Object.keys(selected).length === 0 || !effectiveDate;

  function toggleEng(e: EngagementRow) {
    setSelected((s) => {
      const n = { ...s };
      if (n[e.EngagementID]) delete n[e.EngagementID];
      else n[e.EngagementID] = e;
      return n;
    });
  }

  async function onSubmit() {
    if (!client) return;
    try {
      const res = await submit.mutateAsync({
        clientId: client.clientId,
        clientName: client.clientName,
        engagements: Object.values(selected).map((e) => ({ engagementId: e.EngagementID, serviceCategory: e.ServiceCategory, templateName: e.TemplateName })),
        effectiveDate,
        notes,
      });
      notify('success', `Discontinuation request #${res.submissionId} submitted. A receipt has been emailed to you.`);
      setClientSel([]); setSelected({}); setEffectiveDate(todayISO()); setNotes('');
    } catch (e) {
      notify('error', e instanceof ApiError ? e.problem.title : 'Submit failed');
    }
  }

  return (
    <div className="mx-auto w-full max-w-3xl space-y-4 p-6">
      <div className="card">
        <h2 className="text-lg font-semibold text-primary">Engagement Discontinuation Request</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Select a client, then the specific engagement(s) or service to discontinue, and the effective date. The client
          as a whole remains active. On submit, you receive a receipt and the admin team is notified.
        </p>
      </div>

      <div className="card space-y-4">
        <div>
          <label className="label">Client *</label>
          <MultiClientPicker scope="active" single value={clientSel} disabled={submit.isPending}
            onChange={(v) => { setClientSel(v); setSelected({}); }} />
        </div>

        {client && (
          <div>
            <label className="label">Engagement(s) to discontinue *</label>
            {engagements.isFetching && <p className="text-sm text-muted-foreground">Loading engagements…</p>}
            {!engagements.isFetching && engList.length === 0 && (
              <p className="text-sm text-muted-foreground">No engagements found for this client.</p>
            )}
            {engList.length > 0 && (
              <div className="max-h-72 overflow-auto rounded-md border border-border">
                {engList.map((e) => {
                  const on = !!selected[e.EngagementID];
                  return (
                    <button key={e.EngagementID} type="button" disabled={submit.isPending}
                      className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-secondary/60 ${on ? 'bg-secondary/40' : ''}`}
                      onClick={() => toggleEng(e)}>
                      <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${on ? 'border-primary bg-primary text-primary-foreground' : 'border-border'}`}>
                        {on && <Check className="h-3 w-3" />}
                      </span>
                      <span>
                        <b>{e.EngagementID}</b> · {e.ServiceCategory} / {e.TemplateName} {e.Year ? `(${e.Year})` : ''}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div className="max-w-xs">
          <label className="label">Effective Discontinuation Date *</label>
          <input type="date" className="input" value={effectiveDate} disabled={submit.isPending} onChange={(e) => setEffectiveDate(e.target.value)} />
        </div>

        <div>
          <label className="label">Notes</label>
          <textarea className="input" rows={2} value={notes} disabled={submit.isPending} onChange={(e) => setNotes(e.target.value)} />
        </div>

        <p className="text-xs font-semibold text-accent">*  Mandatory Fields</p>

        <div>
          <button className="btn-primary" disabled={invalid || submit.isPending} onClick={onSubmit}>
            {submit.isPending ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}
